// Reference dossier builder — compiles EVERY source used across the engine (assumptions,
// valorization pathways, literature bounds, per-system citations) into a single vettable
// bibliography with a credibility tier on each entry. Powers the in-app "Export references"
// download and the static /references.html snapshot. Pure functions → no React dependency.

import { ASSUMPTIONS, VALORIZATION, STREAM_BASELINES, REFERENCES, SYSTEM_CITATIONS } from "./citations";

export const TIER_META = {
  "peer-reviewed": { label: "Peer-reviewed", color: "#1e8f42", rank: 1 },
  government: { label: "Government / Institutional", color: "#2a6fd6", rank: 2 },
  standard: { label: "Standard / Code", color: "#0f8a8a", rank: 3 },
  vendor: { label: "Sample vendor / Project data", color: "#c8821f", rank: 4 },
  industry: { label: "Industry estimate", color: "#8a8a8f", rank: 5 },
};

// Infer a credibility tier from the URL when an entry has no explicit `tier`.
export function inferTier(url, explicit) {
  if (explicit && TIER_META[explicit]) return explicit;
  const u = (url || "").toLowerCase();
  if (!u) return "vendor";
  if (u.includes("doi.org") || u.includes("researchgate.net") || u.includes("ncbi.nlm.nih.gov") ||
      u.includes("frontiersin.org") || u.includes("/science/article") || u.includes("adsabs.harvard.edu") ||
      u.includes("theseus.fi") || u.includes("scholarsmine")) return "peer-reviewed";
  if (u.includes("asce.org") || u.includes("iccsafe.org") || u.includes("smartfreightcentre.org") ||
      u.includes("circularecology.com") || u.includes("greenrooforganisation.org") || u.includes("ashrae.org")) return "standard";
  if (u.includes(".gov") || u.includes("fao.org") || u.includes("unep.org") || u.includes("ieabioenergy.com") ||
      u.includes("nrel.gov") || u.includes(".edu") || u.includes("extension") || u.includes("ces.ncsu.edu") ||
      u.includes("cer-rec.gc.ca") || u.includes("biochar-international.org") || u.includes("solarheateurope.eu")) return "government";
  return "vendor";
}

// Normalize every source map into a flat, grouped list of citation rows.
export function collectReferences() {
  const rows = [];
  const push = (group, backs, citation, url, tier) =>
    rows.push({ group, backs, citation, url: url || "", tier: inferTier(url, tier) });

  Object.values(ASSUMPTIONS).forEach((a) => {
    (a.refs || []).forEach((r) => push("Mass / energy-balance assumptions", a.title, r.label, r.url));
  });
  VALORIZATION.forEach((v) => {
    if (v.ref) push("Waste-to-resource valorization", `${v.tech} (${v.input} → ${v.output})`, v.ref.label, v.ref.url);
  });
  Object.entries(REFERENCES).forEach(([key, r]) => {
    const cite = `${r.author} (${r.year}). ${r.title}.`;
    push("Baseline literature (systems & crops)", key, cite, r.url);
  });
  Object.entries(SYSTEM_CITATIONS).forEach(([key, s]) => {
    push("Per-system defaults (CAPEX / OPEX / structural / resource)", s.display || key, s.full || s.display, s.url, s.tier);
  });

  rows.sort((a, b) => (a.group.localeCompare(b.group)) ||
    (TIER_META[a.tier].rank - TIER_META[b.tier].rank) || a.backs.localeCompare(b.backs));
  return rows;
}

export function tierCounts(rows) {
  const c = {};
  rows.forEach((r) => { c[r.tier] = (c[r.tier] || 0) + 1; });
  return c;
}

const esc = (s) => String(s == null ? "" : s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function buildReferencesHTML(rows = collectReferences()) {
  const counts = tierCounts(rows);
  const when = new Date().toISOString().slice(0, 10);
  const groups = [...new Set(rows.map((r) => r.group))];
  const badge = (t) => `<span style="display:inline-block;padding:2px 9px;border-radius:999px;font-size:11px;font-weight:700;color:#fff;background:${TIER_META[t].color}">${TIER_META[t].label}</span>`;
  const legend = Object.keys(TIER_META).filter((t) => counts[t])
    .map((t) => `<span style="margin-right:14px;white-space:nowrap">${badge(t)} <strong>${counts[t]}</strong></span>`).join("");
  const section = (g) => {
    const items = rows.filter((r) => r.group === g).map((r) => `
      <tr>
        <td style="padding:10px 12px;border-bottom:1px solid #eee;vertical-align:top;width:220px">${esc(r.backs)}</td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee;vertical-align:top">${esc(r.citation)}
          ${r.url ? `<div style="margin-top:4px"><a href="${esc(r.url)}" target="_blank" rel="noreferrer" style="color:#2a6fd6;word-break:break-all;font-size:12px">${esc(r.url)}</a></div>`
            : `<div style="margin-top:4px;color:#8a8a8f;font-size:12px">Vendor / project-team source — no public link</div>`}
        </td>
        <td style="padding:10px 12px;border-bottom:1px solid #eee;vertical-align:top;white-space:nowrap">${badge(r.tier)}</td>
      </tr>`).join("");
    return `<h2 style="font-size:15px;letter-spacing:.3px;margin:30px 0 8px">${esc(g)} <span style="color:#8a8a8f;font-weight:500">(${rows.filter((r) => r.group === g).length})</span></h2>
      <table style="width:100%;border-collapse:collapse;font-size:13.5px">
        <thead><tr style="text-align:left;color:#6e6e73;font-size:11px;letter-spacing:.6px;text-transform:uppercase">
          <th style="padding:6px 12px">Backs</th><th style="padding:6px 12px">Source</th><th style="padding:6px 12px">Tier</th>
        </tr></thead><tbody>${items}</tbody></table>`;
  };
  return `<!doctype html><html lang="en"><head><meta charset="utf-8"/>
<meta name="viewport" content="width=device-width, initial-scale=1"/>
<title>Resource Circularity Engine — Reference Dossier</title></head>
<body style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',system-ui,sans-serif;color:#1d1d1f;max-width:980px;margin:0 auto;padding:40px 28px;line-height:1.5">
  <div style="font-size:11px;letter-spacing:3px;color:#86868b;font-weight:700;text-transform:uppercase">Resource Circularity Engine</div>
  <h1 style="font-size:26px;letter-spacing:-.5px;margin:6px 0 2px">Reference Dossier</h1>
  <div style="color:#6e6e73;font-size:13px">Every data source behind the engine's assumptions, conversions and defaults · ${rows.length} entries · generated ${when}</div>
  <div style="margin:18px 0;padding:14px 16px;background:#f6f7f8;border-radius:12px;font-size:13px">
    <div style="font-weight:700;margin-bottom:6px">Source policy</div>
    Each source is a peer-reviewed journal article (DOI), a government / standards body (FAO, EPA, GSA, NREL, IEA, UNEP, ASCE, ICC, land-grant University Extension), or a named sample vendor / project dataset — tagged explicitly. Consumer cost-aggregator and SEO blog sources have been removed. Cost figures with no rigorous source are labelled as vendor / industry estimates.
    <div style="margin-top:10px">${legend}</div>
  </div>
  ${groups.map(section).join("")}
  <footer style="margin-top:40px;padding-top:14px;border-top:1px solid #eee;color:#86868b;font-size:11px">Resource Circularity Engine · www.resourcecircularity.com · Reference dossier ${when}</footer>
</body></html>`;
}

export function buildReferencesCSV(rows = collectReferences()) {
  const q = (s) => `"${String(s == null ? "" : s).replace(/"/g, '""')}"`;
  const head = ["Group", "Backs", "Source", "URL", "Tier"].join(",");
  const body = rows.map((r) => [q(r.group), q(r.backs), q(r.citation), q(r.url), q(TIER_META[r.tier].label)].join(",")).join("\n");
  return `${head}\n${body}\n`;
}

function triggerDownload(filename, mime, text) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

export function downloadReferencesHTML() {
  triggerDownload("rcengine-references.html", "text/html;charset=utf-8", buildReferencesHTML());
}
export function downloadReferencesCSV() {
  triggerDownload("rcengine-references.csv", "text/csv;charset=utf-8", buildReferencesCSV());
}
